import * as React from "react";
import { observer } from "mobx-react-lite";
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Spinner } from "@material-tailwind/react";

import { alertController } from "../../../Common/Controllers/AlertController";
import { userAccountController } from "../../../Common/Controllers/UserAccountController";
import { orderSubpageController } from "../Controllers/OrderSubpageController";
import { StockistPurchaseModel } from "../../../Common/DTOs/Purchase/StockistPurchaseModel";
import LoadingOverlay from "../../../Common/Components/LoadingOverlay";
import { Couriers } from "../../../Common/DTOs/Delivery/CourierShipmentResponseModel";

export const OrdersSubpage = observer(() => {
  const navigate = useNavigate();

  useEffect(() => {
    if (!userAccountController.isLoggedIn && !userAccountController.isUserAccountDataLoading) {
      alertController.displayAlert(
        "Login required",
        "You have to be logged in in order to view this page.",
        "warning",
        4000
      );
      navigate("/latest");
      return;
    }
  }, []);

  return (
    <div className="w-full flex flex-col gap-4 py-2">
      {orderSubpageController.stockistOrders.length === 0 ? (
        <div className="p-6 text-gray-500 text-center">No orders found.</div>
      ) : (
        orderSubpageController.stockistOrders.map((order) => (
          <OrderBlock key={order.id} order={order} />
        ))
      )}
      <LoadingOverlay isVisible={orderSubpageController.isStockistOrdersLoading} />
    </div>
  );
});

const OrderBlock = observer(({ order }: { order: StockistPurchaseModel }) => {
  return (
    <div className="w-full bg-gray-800 border rounded-lg shadow-sm p-4">
      <div className="flex flex-col gap-4 sm:flex-row justify-stretch sm:justify-between sm:items-center">
        <div>
          <div className="flex gap-1">
            <div className="font-semibold text-xl">{order.statusText}</div>
            {order.lastAction && <span className="text-sm text-gray-400 italic">{order.lastAction.toLocaleDateString("en-GB")}</span>}
          </div>
          <div className="h-[1px] mb-2 bg-gray-600"></div>
          <div className="text-sm text-white">
            ID: {order.id}
          </div>
          <div className="text-sm text-white mt-1">
            {Couriers[order.courier]} | {order.serviceType}
          </div>
          {order.declaredDelivery &&
            <div className="text-sm text-white mt-1">
              Est. delivery: {order.declaredDelivery?.toLocaleDateString("en-GB")}
            </div>
          }
          {order.trackingNumber &&
            <div className="text-sm text-white mt-1">
              <span className="mr-2">Tracking:</span><span className="text-light-blue-700 hover:text-light-blue-900 text-sm italic font-normal transition duration-150"><a href={`${order.trackingNumberUrl}`} target="_blank">2234234{order.trackingNumber}</a></span>
            </div>
          }
          <div className="text-white mt-5">
            <span className="font-semibold text-xl">{order.currencySymbol}{order.transactionValue.toFixed(2)}</span>
            <span className="ml-2 mr-1 text-xs text-gray-400 italic">incl. postage:</span><span className="text-sm font-semibold">{order.currencySymbol}{order.publisherPrice.toFixed(2)}</span>
          </div>
        </div>

        <button
          onClick={() => order.toggleExpanded()}
          className="text-white hover:text-blue-800"
        >
          {order.isExpanded ? "▲ Hide details" : "▼ Show details"}
        </button>
      </div>
      {order.hasInvoice &&
        <>
          <div className="h-[1px] mb-2 bg-gray-600"></div>
          <Button
            size="sm"
            onClick={() => order.openInvoiceLink()}
            disabled={!order.hasInvoice}
            className="bg-main hover:bg-gray-600 border border-white">
            Download Invoice
          </Button>
        </>
      }

      {order.isExpanded && (
        <div className="mt-4 border-t-2 pt-3">
          {order.isItemsLoading ? (
            <div className="flex justify-center py-4">
              <Spinner />
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="border-b">
                <tr>
                  <th className="text-left py-1">Magazine</th>
                  <th className="text-left py-1">Issue</th>
                  <th className="text-right py-1">Qty</th>
                  <th className="text-right py-1">Disc. Issue Price</th>
                  <th className="text-right py-1">Total Value</th>
                </tr>
              </thead>
              <tbody>
                {order.items.map((item) => (
                  <tr key={item.issueId} className="border-b last:border-0">
                    <td>{item.magazineTitle}</td>
                    <td>{item.issueTitle}</td>
                    <td className="text-right">{item.quantity}</td>
                    <td className="text-right">
                      {order.currencySymbol}{item.finalPrice.toFixed(2)}
                    </td>
                    <td className="text-right">
                      {order.currencySymbol}{(item.finalPrice * item.quantity).toFixed(2)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
});
